import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileStack, Users, Tags, Users2, Megaphone, FileText,
  Globe, ScrollText, Settings, LogOut, Menu as MenuIcon, X, ClipboardList, KeyRound, GraduationCap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const ALL_MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, roles: null },
  { to: "/admin/permohonan", label: "Permohonan", icon: FileStack, roles: ["super_admin", "secretariat", "president"] },
  { to: "/admin/ahli", label: "Ahli", icon: Users, roles: null },
  { to: "/admin/kategori", label: "Kategori Keahlian", icon: Tags, roles: ["super_admin", "secretariat", "president"] },
  { to: "/admin/jawatankuasa", label: "Jawatankuasa", icon: Users2, roles: ["super_admin", "president"] },
  { to: "/admin/jurulatih", label: "Jurulatih", icon: GraduationCap, roles: ["super_admin", "president"] },
  { to: "/admin/pengumuman", label: "Pengumuman", icon: Megaphone, roles: ["super_admin", "secretariat", "president"] },
  { to: "/admin/kursus-leads", label: "Pendaftaran Kursus", icon: ClipboardList, roles: ["super_admin", "president"] },
  { to: "/admin/pengguna-staf", label: "Pengguna Staf", icon: KeyRound, roles: ["super_admin", "president"] },
  { to: "/admin/dokumen", label: "Dokumen", icon: FileText, roles: ["super_admin", "secretariat", "president"] },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "president"] },
  { to: "/admin/tetapan", label: "Tetapan", icon: Settings, roles: ["super_admin", "president"] },
];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  secretariat: "Urusetia",
  president: "Presiden",
  treasurer: "Bendahari",
  committee: "AJK",
  verifier: "Pemeriksa",
};

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menu = ALL_MENU.filter((m) => !m.roles || m.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2.5 px-5 py-6 border-b border-white/10">
        <img src="/piks-logo.png?v=7" alt="Bodyguard Sarawak" className="h-9 w-9 object-contain shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm tracking-wide">BODYGUARD SARAWAK</div>
          <div className="text-[10px] text-piks-gold/80">Admin Console</div>
        </div>
        <button className="lg:hidden text-white/70 hover:text-white shrink-0" onClick={() => setOpen(false)} data-testid="admin-mobile-menu-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {menu.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            onClick={() => setOpen(false)}
            data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-piks-gold text-piks-black" : "text-white/75 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="w-4 h-4" /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3.5 py-2 mb-1">
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-[11px] text-piks-gold/80">{ROLE_LABEL[user?.role] || user?.role}</p>
        </div>
        <ChangePasswordDialog triggerClassName="flex items-center gap-3 px-3.5 py-2.5 w-full rounded-lg text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white" />
        <button onClick={handleLogout} data-testid="admin-sidebar-logout-btn" className="flex items-center gap-3 px-3.5 py-2.5 w-full rounded-lg text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white">
          <LogOut className="w-4 h-4" /> Log Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-piks-ivory flex" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col w-64 bg-piks-black text-white shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-piks-black text-white flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-piks-border h-16 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden text-piks-black" onClick={() => setOpen(true)} data-testid="admin-mobile-menu-toggle">
            <MenuIcon className="w-6 h-6" />
          </button>
          <p className="hidden lg:block text-sm font-semibold text-piks-black">Panel Pentadbiran Bodyguard Sarawak</p>
          <div className="flex items-center gap-2 text-xs text-piks-muted">
            <span className="hidden sm:inline">{user?.name}</span>
            <span className="px-2 py-1 rounded-full bg-piks-gold/20 text-piks-black font-semibold text-[11px]">{ROLE_LABEL[user?.role] || user?.role}</span>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
